// Stale-deploy recovery (2026-09-29): after a redeploy an open tab still asks for the previous build's chunk hashes,
// which no longer exist, and the page went blank. A failed chunk/preload reloads the page ONCE (sessionStorage guard so
// it can never loop); anything else that crashes the tree shows a bilingual "reload" screen instead of a white page.
import * as React from 'react';

const KEY = 'vs.chunkReloadAt';
const WINDOW_MS = 60_000;

/** A failed dynamic import / module preload (Chrome, Firefox, Safari wording, Vite's preload error). Pure. */
export function isChunkError(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : String(e ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed|Outdated Optimize Dep/i.test(msg);
}

type Store = Pick<Storage, 'getItem' | 'setItem'> | null;
function session(): Store { try { return window.sessionStorage; } catch { return null; } }

/** True (and the caller reloads) unless a reload for the same reason already happened in the last minute. Pure over
 *  `s` and `now`; without storage it refuses, so a blocked sessionStorage can never cause a loop. */
export function shouldReload(s: Store, now: number): boolean {
  try {
    if (!s) return false;
    const last = Number(s.getItem(KEY) ?? 0);
    if (last && now - last < WINDOW_MS) return false;
    s.setItem(KEY, String(now));
    return true;
  } catch { return false; }
}

export function reloadOnce(): boolean {
  if (!shouldReload(session(), Date.now())) return false;
  window.location.reload();
  return true;
}

/** Page-level listeners: Vite's preload error and unhandled dynamic-import rejections. */
export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (e) => { if (reloadOnce()) e.preventDefault(); });
  window.addEventListener('unhandledrejection', (e) => { if (isChunkError(e.reason)) reloadOnce(); });
}

/** Text outside the i18n provider (it may be what crashed): Hebrew when the page is Hebrew. */
export function crashText(lang: string): { title: string; body: string; button: string } {
  return lang.startsWith('he')
    ? { title: 'משהו השתבש — טען מחדש', body: 'ייתכן שהגרסה של Studio עודכנה בזמן שהדף היה פתוח.', button: 'טעינה מחדש' }
    : { title: 'Something went wrong — reload the page', body: 'Studio may have been updated while this page was open.', button: 'Reload' };
}

export function ReloadScreen() {
  const txt = crashText(typeof document !== 'undefined' ? document.documentElement.lang || navigator.language : 'en');
  return (
    <main className="mx-auto mt-24 max-w-lg p-6 text-center" role="alert">
      <h1 className="text-xl font-semibold">{txt.title}</h1>
      <p className="mt-2 text-sm text-fg-muted">{txt.body}</p>
      <button type="button" onClick={() => window.location.reload()}
        className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus">
        {txt.button}
      </button>
    </main>
  );
}

/** Top-level boundary: never a blank white page. A chunk error first tries the one-time reload. */
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) { return { error }; }
  componentDidCatch(error: unknown) { if (isChunkError(error)) reloadOnce(); }
  render() { return this.state.error ? <ReloadScreen /> : this.props.children; }
}
